<!-- AuthCard.vue -->
<template>
	<div class="min-h-screen bg-gray-50 flex">
		<!-- Left Panel - Brand Banner -->
		<div class="hidden lg:flex relative flex-1">
			<div class="flex-1 bg-gradient-to-br from-primary to-blue-800">
				<!-- Geometric Pattern Background -->
				<div class="absolute inset-0 opacity-10">
					<svg
						class="absolute inset-0 h-full w-full"
						xmlns="http://www.w3.org/2000/svg"
					>
						<defs>
							<pattern
								id="geometric-pattern"
								x="0"
								y="0"
								width="40"
								height="40"
								patternUnits="userSpaceOnUse"
							>
								<circle
									cx="20"
									cy="20"
									r="2"
									fill="currentColor"
								/>
								<path
									d="M20 0 L40 20 L20 40 L0 20 Z"
									fill="none"
									stroke="currentColor"
									stroke-width="1"
								/>
							</pattern>
						</defs>
						<rect
							width="100%"
							height="100%"
							fill="url(#geometric-pattern)"
						/>
					</svg>
				</div>

				<!-- Logo Area -->
				<div class="absolute top-8 left-8 z-10">
					<router-link
						to="/"
						class="flex items-center"
						@click.native="onLogoClick"
						@keydown.native.prevent="onLogoKeydown"
					>
						<div class="logo-wrapper flex items-center">
							<div
								:class="[
									'w-24 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm',
									{
										'logo-clickable': true,
										'pulse-animate': logoAnimating,
									},
								]"
							>
								<img
									src="/assets/Logo/nexus.svg"
									alt="Nexus Logo"
									class="w-28 h-28 object-contain"
									@error="onImgError"
								/>
								<svg
									style="display: none"
									class="w-7 h-7 text-white"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
								</svg>
							</div>
							<span
								:class="[
									'ml-3 text-2xl font-bold text-white cursor-pointer',
									{
										'logo-clickable': true,
										'pulse-animate': logoAnimating,
									},
								]"
								>ClientPortal</span
							>
						</div>
					</router-link>
				</div>

				<!-- Content -->
				<div
					class="relative flex flex-col justify-center h-full px-12 text-white"
				>
					<div class="max-w-md mx-auto">
						<h3 class="text-4xl font-bold leading-tight mb-6">
							Your Security, Simplified
						</h3>
						<p class="text-xl leading-relaxed opacity-90 mb-8">
							Comprehensive insurance solutions tailored to
							protect what matters most to you.
						</p>

						<!-- Feature Points -->
						<div class="space-y-4">
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>Instant Policy Quotes</span
								>
							</div>
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>24/7 Customer Support</span
								>
							</div>
							<div class="flex items-center">
								<div
									class="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3"
								>
									<svg
										class="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
								<span class="text-lg opacity-90"
									>Secure Online Portal</span
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Right Panel - Form Section -->
		<div
			class="flex-1 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-12 xl:px-16 bg-white"
		>
			<div class="mx-auto w-full max-w-md mobile-card lg:mobile-card-lg">
				<div class="lg:hidden mb-6 text-center">
					<div class="flex justify-center items-center mb-2">
						<router-link
							to="/"
							class="flex items-center"
							@click.native="onLogoClick"
							@keydown.native.prevent="onLogoKeydown"
						>
							<div class="logo-wrapper flex items-center">
								<div
									:class="[
										'w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center mr-3',
										{
											'logo-clickable': true,
											'pulse-animate': logoAnimating,
										},
									]"
								>
									<img
										src="/assets/Logo/nexus.svg"
										alt="Company Logo"
										class="w-8 h-8 object-contain"
										@error="onImgError"
									/>
								</div>
								<span
									:class="[
										'text-2xl font-bold text-gray-900 cursor-pointer',
										{
											'logo-clickable': true,
											'pulse-animate': logoAnimating,
										},
									]"
									>InsurePortal</span
								>
							</div>
						</router-link>
					</div>
					<p class="text-sm text-gray-600">
						Your Security, Simplified
					</p>
				</div>

				<!-- Initial Prompt -->
				<div v-if="!showForm" class="space-y-8">
					<div>
						<h2 class="text-3xl font-bold text-gray-900 mb-3">
							Continue Your Application
						</h2>
						<p class="text-gray-600 text-base leading-relaxed">
							To proceed with your insurance application, please
							sign in or create an account.
						</p>
					</div>

					<div class="space-y-6">
						<p class="text-gray-900 font-semibold text-lg">
							Do you have an account?
						</p>

						<div class="space-y-4">
							<button
								@click="
									showForm = true;
									isLogin = true;
								"
								class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
							>
								Yes, Sign In
							</button>

							<button
								@click="
									showForm = true;
									isLogin = false;
								"
								class="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
							>
								No, Create Account
							</button>
						</div>
					</div>
				</div>

				<!-- Form Section -->
				<div v-else class="space-y-8">
					<!-- Header -->
					<div>
						<h2 class="text-3xl font-bold text-gray-900 mb-3">
							{{ headingText }}
						</h2>
						<p class="text-gray-600 text-base leading-relaxed">
							{{
								isLogin
									? 'Sign in to access your insurance portal'
									: 'Join us to explore personalized insurance solutions'
							}}
						</p>
					</div>

					<!-- Form -->
					<form @submit.prevent="handleSubmit" class="space-y-6">
						<!-- First Name and Last Name (Register only) -->
						<div
							v-if="!isLogin"
							class="grid grid-cols-1 gap-4 sm:grid-cols-2"
						>
							<div v-if="!isPasswordReset">
								<label
									for="firstName"
									class="block text-sm font-medium text-gray-700 mb-1"
								>
									First Name
								</label>
								<input
									id="firstName"
									v-model="form.firstName"
									type="text"
									required
									class="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 sm:text-sm transition-all duration-200"
									placeholder="First name"
								/>
							</div>
							<div v-if="!isPasswordReset">
								<label
									for="lastName"
									class="block text-sm font-medium text-gray-700 mb-1"
								>
									Last Name
								</label>
								<input
									id="lastName"
									v-model="form.lastName"
									type="text"
									required
									class="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 sm:text-sm transition-all duration-200"
									placeholder="Last name"
								/>
							</div>
						</div>

						<!-- Email -->
						<div v-if="!isPasswordReset">
							<label
								for="email"
								class="block text-sm font-medium text-gray-700 mb-1"
							>
								Email Address
							</label>
							<input
								id="email"
								v-model="form.email"
								type="email"
								required
								class="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 sm:text-sm transition-all duration-200"
								placeholder="Enter your email"
							/>
						</div>
						<div v-if="isPasswordReset">
							<label
								for="email"
								class="block text-sm font-medium text-gray-700 mb-1"
							>
								Email Address
							</label>
							<input
								id="email"
								v-model="form.email"
								type="email"
								required
								class="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 sm:text-sm transition-all duration-200"
								placeholder="Enter your email"
							/>
						</div>
						<section v-if="!isLogin && !isPasswordReset">
							<label
								class="block text-sm font-medium text-gray-700 mb-1"
								>Account Type</label
							>
							<div
								class="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center md:justify-start"
							>
								<!-- Individual Card -->
								<label
									class="group relative flex flex-col items-center justify-center w-full sm:w-48 h-32 bg-white border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
									:class="{
										'border-blue-600 bg-blue-50 shadow-md':
											form.accountType === 'individual',
										'border-gray-300 hover:border-blue-400':
											form.accountType !== 'individual',
									}"
								>
									<input
										v-model="form.accountType"
										type="radio"
										value="individual"
										name="accountType"
										class="absolute opacity-0 w-0 h-0"
										required
									/>

									<div
										class="flex flex-col items-center gap-3 p-4"
									>
										<div
											class="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors"
										>
											<svg
												class="w-7 h-7"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
												/>
											</svg>
										</div>
										<span
											class="text-lg font-medium text-gray-800 group-hover:text-blue-700 transition-colors"
										>
											Individual
										</span>
									</div>

									<!-- Visual selected indicator -->
									<div
										v-if="form.accountType === 'individual'"
										class="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full border-4 border-white flex items-center justify-center"
									>
										<svg
											class="w-4 h-4 text-white"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fill-rule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clip-rule="evenodd"
											/>
										</svg>
									</div>
								</label>

								<!-- Company Card -->
								<label
									class="group relative flex flex-col items-center justify-center w-full sm:w-48 h-32 bg-white border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-indigo-500 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2"
									:class="{
										'border-indigo-600 bg-indigo-50 shadow-md':
											form.accountType === 'company',
										'border-gray-300 hover:border-indigo-400':
											form.accountType !== 'company',
									}"
								>
									<input
										v-model="form.accountType"
										type="radio"
										value="company"
										name="accountType"
										class="absolute opacity-0 w-0 h-0"
										required
									/>

									<div
										class="flex flex-col items-center gap-3 p-4"
									>
										<div
											class="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-200 transition-colors"
										>
											<svg
												class="w-7 h-7"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5"
												/>
											</svg>
										</div>
										<span
											class="text-lg font-medium text-gray-800 group-hover:text-indigo-700 transition-colors"
										>
											Company
										</span>
									</div>

									<!-- Visual selected indicator -->
									<div
										v-if="form.accountType === 'company'"
										class="absolute -top-2 -right-2 w-6 h-6 bg-indigo-600 rounded-full border-4 border-white flex items-center justify-center"
									>
										<svg
											class="w-4 h-4 text-white"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fill-rule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clip-rule="evenodd"
											/>
										</svg>
									</div>
								</label>
							</div>
						</section>

						<!-- Password (Login only) -->
						<div
							v-if="isLogin && !isPasswordReset"
							class="relative"
						>
							<label
								for="password"
								class="block text-sm font-medium text-gray-700 mb-1"
							>
								Password
							</label>
							<input
								id="password"
								v-model="form.password"
								:type="showPassword ? 'text' : 'password'"
								required
								class="appearance-none block w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary focus:ring-2 focus:ring-opacity-20 sm:text-sm transition-all duration-200"
								placeholder="Enter your password"
							/>
							<button
								type="button"
								@click="showPassword = !showPassword"
								class="absolute inset-y-0 right-0 top-6 pr-3 flex items-center text-gray-400 hover:text-gray-600"
							>
								<svg
									v-if="showPassword"
									class="h-5 w-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
									/>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
									/>
								</svg>
								<svg
									v-else
									class="h-5 w-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
									/>
								</svg>
							</button>
						</div>

						<!-- Error Message -->
						<div v-if="error" class="rounded-lg bg-red-50 p-4">
							<div class="text-sm text-red-700">{{ error }}</div>
						</div>
						<button
							v-if="!isPasswordReset"
							type="button"
							@click="forgotPassword"
							class="font-semibold text-brand-crimsonDark"
						>
							Forgot Password?
						</button>
						<!-- Submit Button -->
						<button
							type="submit"
							v-if="!isPasswordReset"
							:disabled="loading"
							class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
						>
							<span v-if="loading" class="flex items-center">
								<svg
									class="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								Processing...
							</span>
							<span v-else>{{
								isLogin ? 'Sign In' : 'Create Account'
							}}</span>
						</button>
						<button
							type="submit"
							v-if="isPasswordReset"
							:disabled="loading"
							class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
						>
							<span v-if="loading" class="flex items-center">
								<svg
									class="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									></path>
								</svg>
								Processing...
							</span>
							<span v-else>Reset Password</span>
						</button>
					</form>

					<!-- Footer Links -->
					<div class="space-y-4">
						<div class="text-center">
							<span class="text-gray-600 text-sm">
								{{
									isLogin
										? "Don't have an account?"
										: 'Already have an account?'
								}}
							</span>
							<button
								v-if="!isPasswordReset"
								@click="isLogin = !isLogin"
								class="ml-1 text-primary hover:text-blue-700 font-medium text-sm transition-colors duration-200"
							>
								{{ isLogin ? 'Create Account' : 'Sign In' }}
							</button>
							<button
								@click="setLogin"
								class="text-primary"
								v-if="isPasswordReset"
							>
								Login
							</button>
						</div>

						<div class="text-center">
							<!-- Shadcn-style back link: subtle, accessible, small footprint on mobile -->
							<button
								@click="showForm = false"
								class="back-to-options"
								aria-label="Back to options"
								type="button"
							>
								<svg
									class="back-icon"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									aria-hidden="true"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 19l-7-7 7-7"
									/>
								</svg>
								<span>Back to options</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Removed fixed mobile header to keep mobile focused on form only -->
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
	ClientSignIn,
	FetchClientDetails,
	ForgorPasswordRequest,
	RegisterClient,
} from '../api/auth';
import { CompleteQuotation } from '../api/insurance';

export default {
	name: 'AuthCard',
	data() {
		return {
			isLogin: true,
			showForm: false,
			showPassword: false,
			logoAnimating: false,
			form: {
				firstName: '',
				lastName: '',
				email: '',
				password: '',
				accountType: '',
			},
			error: '',
			loading: false,
		};
	},
	computed: {
		...mapGetters('auth', ['selectPurchaseType', 'isPasswordReset']),
		...mapGetters('kyc', ['selectKycData']),
		...mapGetters('quotation', [
			'selectSystemQuotation',
			'selectedQuotationReview',
		]),
		headingText() {
			if (this.isLogin) return 'Welcome Back';
			if (this.isPasswordReset) return 'Reset Password';
			return 'Create Account';
		},
	},
	methods: {
		...mapActions('auth', [
			'setAuthData',
			'setRegId',
			'setIsAuthenticated',
			'setRefreshToken',
			'setAuthToken',
			'setIsPasswordReset',
			'setPasswordRef',
		]),
		...mapActions('kyc', ['setKycData']),
		...mapActions('quotation', ['setQuotationData', 'setInvoiceRef']),
		// Fallback for broken images: hide the <img> and show the next sibling (SVG fallback)
		onImgError(event) {
			try {
				const img = event.target;
				img.style.display = 'none';
				const next = img.nextElementSibling;
				if (next) next.style.display = 'block';
			} catch (e) {
				// silent fail — non-critical
			}
		},
		// Play a short pulse animation on the logo/text, then navigate home
		onLogoClick(event) {
			// prevent default router-link navigation; we'll navigate after animation
			try {
				event.preventDefault();
			} catch (e) {}

			if (this.logoAnimating) return;
			this.logoAnimating = true;

			const linkEl = event.currentTarget || event.target;
			const wrapper =
				(linkEl &&
					linkEl.querySelector &&
					linkEl.querySelector('.logo-wrapper')) ||
				linkEl;
			const elems = wrapper
				? wrapper.querySelectorAll('.logo-clickable')
				: [];

			elems.forEach((el) => el.classList.add('pulse-animate'));

			// Wait for the animation to finish, then navigate
			setTimeout(() => {
				elems.forEach((el) => el.classList.remove('pulse-animate'));
				this.logoAnimating = false;
				// Programmatic navigation (safe even if already on /)
				this.$router.push('/');
			}, 220);
		},
		onLogoKeydown(event) {
			const key = event.key || event.keyCode;
			if (key === 'Enter' || key === ' ' || key === 13 || key === 32) {
				// Reuse click handler behavior
				this.onLogoClick(event);
			}
		},
		signUp() {
			return {
				email: this.form.email,
				names: {
					firstName: this.form.firstName,
					lastName: this.form.lastName,
				},
			};
		},
		forgotPassword() {
			this.setIsPasswordReset(true);

			// this.$router.push("/auth/resetpassword")
		},
		login() {
			return {
				username: this.form.email,
				password: this.form.password,
				loginDevice: {},
			};
		},
		setLogin() {
			this.setIsPasswordReset(false);
		},
		async handleSubmit() {
			this.error = '';
			this.loading = true;

			try {
				if (!this.isLogin && !this.isPasswordReset) {
					// Registration flow
					console.log('This is a signup process');
					try {
						const res = await RegisterClient(this.signUp());
						if (res.status) {
							this.setAuthData(this.signUp());
							this.setRegId(res.data); // adjust key according to API
							this.$router.push({
								name: 'Otp_Verification',
								params: { email: this.form.email },
							});
							Event.$emit('notifications:show', {
								type: 'success',
								title: 'Registration successfull',
								message: `You will receive an otp to your email`,
								duration: 3000,
							});
						}
					} catch (error) {
						let errorMessage = '';
						if (error.response.data) {
							errorMessage = error.response.data.errors;
						}
						this.error = errorMessage;

						Event.$emit('notifications:show', {
							type: 'error',
							title: 'Registration error',
							message: `${errorMessage}. Please try again`,
							duration: 3000,
						});
					}
				} else if (!this.isPasswordReset) {
					// Login flow
					const res = await ClientSignIn(this.login());

					if (res.status) {
						if (res.data) {
							this.setAuthData(res.data.user);
							this.setAuthToken(res.data.accessToken);
							this.setRefreshToken(res.data.refreshToken);
							this.setIsAuthenticated(true);
						}

						const response = await FetchClientDetails({});
						if (response.status) {
							if (response.data) {
								this.setKycData(response.data);
							}
						}
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Login Successfull',
							message: `You have been logged in`,
							duration: 3000,
						});
						if (this.selectPurchaseType === 'new') {
							this.$router.push('/motor/cover-details');
						} else if (
							this.selectPurchaseType === 'recommendation'
						) {
							const ref = {
								systemQuotationRef: this.selectSystemQuotation,
							};
							const res = await CompleteQuotation(ref);
							if (res.status) {
								const quotationData = {
									coverType: 'comprehensive',
									period:
										this.selectedQuotationReview
											.coverDetails.period || '',
									totalPremium:
										this.selectedQuotationReview
											.premiumAmount,
									vehicleValue:
										this.selectedQuotationReview.riskValue,
								};
								this.setInvoiceRef(res.data.invoiceSystemRef);
								this.setQuotationData(quotationData);
								this.$router.push(
									'/motor/payment-confirmation/payment',
								);
							}
							// this.$router.push("/motor/payment-confirmation/payment")
						} else {
							this.$router.push('/newportal');
						}
					}
				} else {
					const res = await ForgorPasswordRequest({
						username: this.form.email,
					});
					if (res.status) {
						this.$router.push({
							name: 'Otp_Verification',
							params: { ref: res.data.forgotPasswordSystemRef },
						});
						this.setPasswordRef(res.data.forgotPasswordSystemRef);
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'An otp set',
							message: `An otp has been sent to ${this.form.email}`,
							duration: 3000,
						});
					}
				}
			} catch (err) {
				console.log('error', err);
				let errorMessage =
					err.response.data.errors || 'An error occured';

				console.log('error', err);
				if (errorMessage) {
					this.error = errorMessage;
					Event.$emit('notifications:show', {
						type: 'error',
						title: 'Registration error',
						message: `${errorMessage}. PLease try again`,
						duration: 3000,
					});
				}
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>

<style scoped>
/* Mobile padding adjustment for fixed header - removed fixed header, keep spacing sane */
@media (max-width: 1024px) {
	.flex-col {
		padding-top: 0; /* removed extra top space now header is integrated into form */
	}
}

/* Shadcn-inspired mobile card tweaks: subtle border, smaller radius, compact spacing */
.mobile-card {
	background: #fff;
	border-radius: 0.75rem;
	padding: 1rem;
	box-shadow: 0 6px 20px rgba(20, 30, 60, 0.04);
	border: 1px solid rgba(15, 23, 42, 0.04);
	width: 100%;
	box-sizing: border-box;
}

/* Tighter mobile sizing */
@media (max-width: 640px) {
	.mobile-card {
		max-width: 19.5rem;
		padding: 0.6rem;
		margin: 6vh auto;
		max-height: calc(100vh - 12vh);
		overflow-y: auto;
	}
	.mobile-card h2 {
		font-size: 1rem;
		margin-bottom: 0.35rem;
	}
	.mobile-card p {
		font-size: 0.9rem;
		margin-bottom: 0.25rem;
	}
	.mobile-card label {
		font-size: 0.78rem;
	}

	/* Reduce vertical rhythm inside Tailwind-generated space-y wrappers */
	.mobile-card .space-y-8 > * + * {
		margin-top: 0.45rem !important;
	}
	.mobile-card .space-y-6 > * + * {
		margin-top: 0.35rem !important;
	}

	input,
	button,
	select,
	textarea {
		font-size: 14px;
	}
	input[type='text'],
	input[type='email'],
	input[type='password'] {
		padding: 0.55rem 0.65rem;
	}
	button[type='submit'] {
		padding-top: 0.62rem;
		padding-bottom: 0.62rem;
	}

	/* Hide the mobile header/logo in the card on very small phones to save space */
	.mobile-card .lg\\:hidden {
		display: none;
	}
}

/* Very small phones */
@media (max-width: 380px) {
	.mobile-card {
		max-width: 18rem;
		padding: 0.5rem;
		border-radius: 0.5rem;
	}
	.mobile-card h2 {
		font-size: 0.98rem;
	}
	.mobile-card .space-y-8 > * + * {
		margin-top: 0.35rem !important;
	}
	.mobile-card .space-y-6 > * + * {
		margin-top: 0.25rem !important;
	}
	/* hide the logo completely for tiny devices */
	.mobile-card .lg\\:hidden {
		display: none !important;
	}
}

/* Input styles closer to shadcn: subtle border, rounded, inner padding */
input[type='text'],
input[type='email'],
input[type='password'] {
	padding: 0.6rem 0.75rem;
	border-radius: 0.6rem;
	border: 1px solid rgba(15, 23, 42, 0.06);
	background: #fff;
	box-shadow: none;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;
}
input[type='text']::placeholder,
input[type='email']::placeholder,
input[type='password']::placeholder {
	color: rgba(15, 23, 42, 0.35);
}
input[type='text']:focus,
input[type='email']:focus,
input[type='password']:focus {
	border-color: rgba(59, 130, 246, 0.9);
	box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
}

/* Primary submit button: denser, rounded, full width */
button[type='submit'] {
	padding: 0.68rem 0.85rem;
	border-radius: 0.6rem;
}

/* Back-to-options link (shadcn-like) */
.back-to-options {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: rgba(15, 23, 42, 0.65);
	background: transparent;
	border: none;
	font-size: 0.85rem;
	padding: 0.28rem 0.45rem;
	border-radius: 0.45rem;
	cursor: pointer;
	transition:
		background 0.12s ease,
		color 0.12s ease,
		transform 0.08s ease;
}
.back-to-options:hover {
	background: rgba(15, 23, 42, 0.02);
	color: rgba(15, 23, 42, 0.9);
}
.back-to-options:active {
	transform: translateY(1px);
}
.back-to-options:focus {
	outline: none;
	box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
}
.back-to-options .back-icon {
	width: 0.95rem;
	height: 0.95rem;
}

/* Compact the password eye icon on narrow cards */
.mobile-card .relative .absolute.inset-y-0.right-0 {
	top: 8px;
}

/* keep the rest of existing styles below */

/* Enhanced focus effects */
input:focus {
	box-shadow: 0 0 0 2px rgba(17, 159, 225, 0.2);
}

/* Button hover animations */
button {
	transition: all 0.2s ease-in-out;
}

button:hover {
	transform: translateY(-1px);
}

button:active {
	transform: translateY(0);
}

/* Logo fade-in animation */
.flex.items-center {
	animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Pulse animation for logo click */
.pulse-animate {
	animation: pulse 0.2s ease-in-out;
}

@keyframes pulse {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.05);
	}
	100% {
		transform: scale(1);
	}
}
</style>
